import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReplaceQuarryIdMutation, useUpdateQuarryMutation } from '../../../../hooks/mutations/useQuarryMutation';
import { useQuarriesQuery } from '../../../../hooks/queries/useQuarriesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Quarry } from '../../../../services/apiSLTP/sites/quarries';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, localStorageKeys, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import ReplaceItemAdminModal from '../../../modals/admin/ReplaceItemAdminModal';
import UpdateQuarryModal from '../../../modals/admin/external/UpdateQuarryModal';
import DeleteModal from '../../../modals/DeleteModal';
import TableOptionBar from '../../TableOptionBar';
import GenericAgGridTable from '../../GenericAgGridTable';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import IconButton from '../../../shared/IconButton';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface QuarriesSettingsRowProps {
    withUsageCount: boolean;
}

const QuarriesSettingsRow: React.FC<QuarriesSettingsRowProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { data: quarries, isLoading: isQuarriesLoading, refetch: refetchQuarries } = useQuarriesQuery({ queries: { withUsage: withUsageCount } });

    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [selectedQuarry, setSelectedQuarry] = useState<Quarry | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { mutate: mutateQuarry } = useUpdateQuarryMutation({
        onSettled: () => refetchQuarries(),
    });

    const { mutate: mutateReplaceQuarry } = useReplaceQuarryIdMutation({
        onSettled: () => refetchQuarries(),
    });

    const columnDefs: ColDef<Quarry>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Quarry>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyQuarry'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedQuarry(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <>
                                <IconButton
                                    iconName="delete"
                                    title={capitalizeFirstLetter(t('deleteQuarry'))}
                                    onClick={() => {
                                        if (!params.data) return;
                                        setSelectedQuarry(params.data);
                                        setShowDeleteModal(true);
                                    }}
                                    disabled={params.data.crudStatus === crudStatus.DELETED || params.data.usageCount?.total !== 0}
                                />
                                <IconButton
                                    iconName="autoMode"
                                    title={capitalizeFirstLetter(t('replaceQuarry'))}
                                    onClick={() => {
                                        if (!params.data) return;
                                        setSelectedQuarry(params.data);
                                        setShowReplaceModal(true);
                                    }}
                                    disabled={params.data.usageCount?.total === 0}
                                />
                            </>
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Quarry>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('address')),
            field: 'address',
            colId: 'address',
        },
        {
            headerName: capitalizeFirstLetter(t('createdBy')),
            field: 'createdBy',
            valueGetter: (params) => formatName(params.data?.createdBy?.firstName, params.data?.createdBy?.lastName),
            colId: 'createdBy',
        },
        createDateTimeColumn<Quarry>('createdAt', 'createdAt'),
        createDateTimeColumn<Quarry>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                field: 'usageCount.dailyTrucksPlanned',
                colId: 'xridesplanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesCompleted')),
                field: 'usageCount.dailyTrucksRides',
                colId: 'xridescompleted',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xTransfersPlanned')),
                field: 'usageCount.dailyTransfers',
                colId: 'xtransfersplanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xTransfersCompleted')),
                field: 'usageCount.dailyTransferRides',
                colId: 'xtransferscompleted',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    const rowData = useMemo(() => {
        return [...(quarries ?? [])].sort((a, b) => a.designation.localeCompare(b.designation));
    }, [quarries]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                loading={isQuarriesLoading}
                exportCsvFileName="admin-quarries"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedQuarry(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addQuarry'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.QUARRIES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
            {showEditModal && (
                <UpdateQuarryModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    quarry={selectedQuarry}
                    isEditMode={isEditMode}
                    refetch={refetchQuarries}
                />
            )}
            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateQuarry({ quarryId: selectedQuarry?._id || '', data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('quarry')}
                    itemName={selectedQuarry?.designation}
                />
            )}
            {showReplaceModal && (
                <ReplaceItemAdminModal
                    showModal={showReplaceModal}
                    setShowModal={setShowReplaceModal}
                    itemId={selectedQuarry?._id || ''}
                    itemName={selectedQuarry?.designation || ''}
                    replaceItems={quarries?.filter((q) => q.crudStatus === crudStatus.OK).map((q) => ({ label: q.designation, value: q._id })) || []}
                    title={t('quarries')}
                    usageCount={selectedQuarry?.usageCount?.total || -1}
                    handleOk={(replaceId) => mutateReplaceQuarry({ oldId: selectedQuarry?._id || '', newId: replaceId })}
                />
            )}
        </>
    );
};

export default QuarriesSettingsRow;
